import { GiftGroup } from "../../types";

import { GiftGroupsState } from "../../redux-store/reducers/gifts-reducers/giftGroupsReducer";
import { GiftItem, TLoyaltyItem } from "../../types";
import { v4 as uuidv4 } from "uuid";
import { getMedianPrice } from "./getMedianPrice";
import { isPriceInRange } from "./isPriceInRange";

export const addGiftGroupItem = (
  state: GiftGroupsState,
  { group_id, item }: { group_id: string; item: GiftItem | TLoyaltyItem }
): { group_id?: string; state?: GiftGroupsState } => {
  const id = uuidv4();
  const newGroup = {
    id,
    name: `Gift - ${Math.round(item.price)} ${state.currency.en}`,
    ar_name: `هدية - ${Math.round(item.price)} ${state.currency.ar}`,
    items: [{ ...item, order: 1 }],
    price: Math.round(item.price),
    is_renamed: 0 as 0 | 1,
    is_ar_renamed: 0 as 0 | 1,
  };

  const createUpdatedState = (
    updatedById: Record<string, GiftGroup>,
    updatedIds: string[]
  ): GiftGroupsState => ({
    ...state,
    shownGiftGroupsById: updatedById,
    shownGiftGroupsIds: updatedIds,
  });

  const filterEmptyGroups = (
    byId: Record<string, GiftGroup>,
    ids: string[]
  ): { byId: Record<string, GiftGroup>; ids: string[] } => {
    const filteredById = { ...byId };
    const filteredIds = ids.filter((id) => {
      const group = filteredById[id];
      const isEmpty = group.items.length === 1 && !group.items[0].id;
      if (isEmpty) {
        delete filteredById[id];
        return false;
      }
      return true;
    });
    return { byId: filteredById, ids: filteredIds };
  };

  if (group_id) {
    const group = state.shownGiftGroupsById[group_id];
    const firstItem = group?.items[0] as GiftItem;
    const isOutsideRange = !isPriceInRange(firstItem.price, item.price);

    if (isOutsideRange) {
      const updatedById = { ...state.shownGiftGroupsById, [id]: newGroup };
      const updatedIds = Object.keys(updatedById).sort(
        (a, b) => updatedById[a].price - updatedById[b].price
      );

      const { byId: finalById, ids: finalIds } = filterEmptyGroups(
        updatedById,
        updatedIds
      );

      return {
        group_id,
        state: createUpdatedState(finalById, finalIds),
      };
    }

    const items: GiftItem[] = [...group.items] as GiftItem[];
    const idx = items.findIndex((i) => i.id === "");
    if (idx !== -1) {
      items[idx] = item as GiftItem;
    } else {
      items.push(item as GiftItem);
    }

    const updatedById = {
      ...state.shownGiftGroupsById,
      [group_id]: {
        ...group,
        items,
        price: getMedianPrice(items),
      },
    };

    const { byId: finalById, ids: finalIds } = filterEmptyGroups(
      updatedById,
      state.shownGiftGroupsIds
    );

    return {
      group_id,
      state: createUpdatedState(finalById, finalIds),
    };
  }

  for (const group of Object.values(state.shownGiftGroupsById)) {
    const firstItem = group.items[0] as GiftItem;
    const price = Number(firstItem.price || 0);

    if (isPriceInRange(price, item.price)) {
      const items = [
        ...group.items,
        { ...item, order: group.items.length + 1 },
      ];

      return {
        group_id: group.id,
        state: {
          ...state,
          shownGiftGroupsById: {
            ...state.shownGiftGroupsById,
            [group.id]: {
              ...group,
              price: getMedianPrice(items as GiftItem[]),
              items,
            },
          },
        },
      };
    }
  }

  const updatedById = { ...state.shownGiftGroupsById, [id]: newGroup };
  const updatedIds = Object.keys(updatedById).sort(
    (a, b) => updatedById[a].price - updatedById[b].price
  );

  const { byId: finalById, ids: finalIds } = filterEmptyGroups(
    updatedById,
    updatedIds
  );

  return {
    group_id,
    state: createUpdatedState(finalById, finalIds),
  };
};
